import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Time } from '@/types';

/**
 * Replaces per-price redux-form instances with a single Redux slice.
 * At 10,000+ items, individual redux-form instances cause O(n²) Redux overhead.
 * This slice stores all group-booking quantities in a single, efficient structure.
 *
 * Key format: "FromISO|ToISO" → { priceId: quantity }
 */

export const INTERVAL_KEY_SEPARATOR = '|';

export function getIntervalKey(timeSpot: Time): string {
    const from = new Date(timeSpot.From as any).toISOString();
    const to = new Date(timeSpot.To as any).toISOString();
    return `${from}${INTERVAL_KEY_SEPARATOR}${to}`;
}

export function parseSliceIntervalKey(key: string): { From: string; To: string } {
    const [from, to] = key.split(INTERVAL_KEY_SEPARATOR);
    return { From: from, To: to };
}

interface DateQuantitiesFormState {
    /** intervalKey → priceId → quantity */
    quantities: Record<string, Record<number, number>>;
}

const initialState: DateQuantitiesFormState = {
    quantities: {},
};

const dateQuantitiesFormSlice = createSlice({
    name: 'dateQuantitiesForm',
    initialState,
    reducers: {
        /**
         * Set a quantity for a specific price within an interval.
         * If maxQuantity is provided, validates that the total across all prices
         * for this interval does not exceed it — this moves the check into the
         * reducer so individual PriceRow components don't need a totalQuantity selector.
         */
        setQuantity(
            state,
            action: PayloadAction<{
                intervalKey: string;
                priceId: number;
                quantity: number;
                maxQuantity?: number;
            }>
        ) {
            const { intervalKey, priceId, quantity, maxQuantity } = action.payload;

            // Validate max quantity if provided
            if (typeof maxQuantity === 'number') {
                const intervalPrices = state.quantities[intervalKey] || {};
                const currentQty = intervalPrices[priceId] ?? 0;
                const currentTotal = Object.values(intervalPrices).reduce(
                    (sum, qty) => sum + qty,
                    0
                );
                const delta = quantity - currentQty;
                if (currentTotal + delta > maxQuantity) {
                    return; // Would exceed max — reject silently
                }
            }

            if (!state.quantities[intervalKey]) {
                state.quantities[intervalKey] = {};
            }

            if (quantity <= 0) {
                delete state.quantities[intervalKey][priceId];
                // Clean up empty intervals to keep the state sparse
                if (Object.keys(state.quantities[intervalKey]).length === 0) {
                    delete state.quantities[intervalKey];
                }
            } else {
                state.quantities[intervalKey][priceId] = quantity;
            }
        },

        resetQuantities() {
            return initialState;
        },
    },
});

export const { setQuantity, resetQuantities } = dateQuantitiesFormSlice.actions;

// ─── Selectors ───────────────────────────────────────────────────────────────

export const selectQuantity = (
    state: any,
    intervalKey: string,
    priceId: number
): number => state.dateQuantitiesForm?.quantities[intervalKey]?.[priceId] ?? 0;

export const selectAllQuantities = (
    state: any
): Record<string, Record<number, number>> =>
    state.dateQuantitiesForm?.quantities ?? {};

/**
 * Sum of all quantities for an interval.
 * Used by getTotalQuantityForDate (combined with redux-form data for non-group path).
 */
export const selectSliceTotalForInterval = (
    state: any,
    intervalKey: string
): number => {
    const prices = state.dateQuantitiesForm?.quantities[intervalKey];
    if (!prices) return 0;
    return Object.values(prices).reduce((sum: number, qty: any) => sum + (qty as number), 0);
};

export default dateQuantitiesFormSlice;
